import type { ApplicationManifest, ApplicationProps } from '@/applications/types';
import { DEFAULT_CAPABILITIES } from '@/applications/types';
import { asApplicationId, type ObjectId, type SpatialObjectKind, type WorkspaceDocument } from '@/types';
import { OBJECT_KINDS } from '@/core/scene/objectTypes';
import { unionBounds } from '@/core/camera/cameraOps';
import { boundsFromPositionSize } from '@/core/geometry/bounds';
import styles from './AnalyticsApp.module.css';

export interface WorkspaceStats {
  readonly total: number;
  readonly byKind: readonly { kind: SpatialObjectKind; count: number }[];
  readonly connections: number;
  readonly hidden: number;
  readonly locked: number;
  readonly containers: number;
  readonly maxDepth: number;
  readonly extent: { width: number; height: number } | null;
  readonly ageDays: number;
  readonly lastEditedAt: number | null;
}

/** Pure aggregation over a document; unit-tested independently of the UI. */
export const computeStats = (doc: WorkspaceDocument, now = Date.now()): WorkspaceStats => {
  const objects = Object.values(doc.objects);
  const counts = new Map<SpatialObjectKind, number>();
  for (const o of objects) counts.set(o.kind, (counts.get(o.kind) ?? 0) + 1);
  const depthOf = (id: ObjectId, guard = new Set<ObjectId>()): number => {
    const o = doc.objects[id];
    if (o?.parentId == null || guard.has(id)) return 0;
    guard.add(id);
    return 1 + depthOf(o.parentId, guard);
  };
  const roots = objects.filter((o) => o.parentId === null);
  const extent = unionBounds(roots.map((o) => boundsFromPositionSize(o.transform.position, o.size)));
  return {
    total: objects.length,
    byKind: OBJECT_KINDS.map((kind) => ({ kind, count: counts.get(kind) ?? 0 })).filter((x) => x.count > 0),
    connections: Object.keys(doc.connections).length,
    hidden: objects.filter((o) => !o.visible).length,
    locked: objects.filter((o) => o.locked).length,
    containers: objects.filter((o) => o.kind === 'group' || o.kind === 'folder').length,
    maxDepth: objects.reduce((m, o) => Math.max(m, depthOf(o.id)), 0),
    extent: extent
      ? { width: Math.round(extent.maxX - extent.minX), height: Math.round(extent.maxY - extent.minY) }
      : null,
    ageDays: Math.max(0, Math.floor((now - doc.workspace.createdAt) / 86_400_000)),
    lastEditedAt: objects.length ? Math.max(...objects.map((o) => o.metadata.updatedAt)) : null,
  };
};

export const AnalyticsApp = ({ api }: ApplicationProps) => {
  const s = computeStats(api.document);
  const max = Math.max(1, ...s.byKind.map((k) => k.count));
  return (
    <div className={styles.root} data-scrollable>
      <div className={styles.tiles}>
        <Tile label="Objects" value={s.total} testId="analytics-total" />
        <Tile label="Connections" value={s.connections} />
        <Tile label="Containers" value={s.containers} />
        <Tile label="Max depth" value={s.maxDepth} />
        <Tile label="Hidden" value={s.hidden} />
        <Tile label="Locked" value={s.locked} />
        <Tile label="Undo steps" value={api.historyDepth} />
        <Tile label="Age (days)" value={s.ageDays} />
      </div>
      <h3 className={styles.heading}>By kind</h3>
      {s.byKind.length === 0 ? (
        <p className={styles.empty}>Nothing here yet.</p>
      ) : (
        <ul className={styles.bars}>
          {s.byKind.map((k) => (
            <li key={k.kind} className={styles.bar}>
              <span className={styles.barLabel}>{k.kind}</span>
              <span className={styles.barTrack}>
                <span className={styles.barFill} style={{ width: `${(k.count / max) * 100}%` }} />
              </span>
              <span className={styles.barValue}>{k.count}</span>
            </li>
          ))}
        </ul>
      )}
      <p className={styles.foot}>
        {s.extent ? `Content extent ${s.extent.width} × ${s.extent.height} world units.` : 'No content extent.'}{' '}
        {s.lastEditedAt ? `Last edit ${new Date(s.lastEditedAt).toLocaleTimeString()}.` : ''}
      </p>
    </div>
  );
};

const Tile = ({ label, value, testId }: { label: string; value: number; testId?: string }) => (
  <div className={styles.tile}>
    <span className={styles.tileValue} data-testid={testId}>
      {value}
    </span>
    <span className={styles.tileLabel}>{label}</span>
  </div>
);

export const analyticsManifest: ApplicationManifest = {
  id: asApplicationId('app.analytics'),
  name: 'Analytics',
  version: '1.0.0',
  icon: '▥',
  category: 'insights',
  description: 'Live figures about this workspace: counts, structure, activity.',
  defaultSize: { width: 380, height: 440 },
  defaultPosition: 'cascade',
  capabilities: { ...DEFAULT_CAPABILITIES, multiInstance: false, persistsState: false, observesWorkspace: true },
  component: AnalyticsApp,
  normalizeState: () => ({}),
};
